'use client'

import { useState, useRef, useEffect, useCallback } from 'react'
import type { ToolKey, ToolState, Insight, ActionItem } from '@/types/radar'
import { usePrefersReducedMotion } from '@/components/chat/useVectorChat'
import { platformFetch } from '@/lib/platform/client-headers'

export interface AdvisorMessage {
  id: number
  role: 'user' | 'advisor'
  content: string
  fullContent?: string
  relatedEntities?: { name: string; url: string; type: string }[]
}

export const FREE_TIER_MESSAGE_CAP = 5

let msgIdCounter = 0

function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/#{1,6}\s/g, '')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    .trim()
}

interface UseAdvisorChatOptions {
  domain: string | null
  auditContext: string
  toolResults?: Record<ToolKey, ToolState>
  insights?: Insight[]
  actions?: ActionItem[]
  userTier?: 'free' | 'paid'
}

/**
 * useAdvisorChat — the data layer of the Readiness Strategist, extracted from
 * AdvisorChat.tsx. Owns messages, the single canonical send path, the free-tier
 * gate math, the typewriter reveal (with skip + reduced-motion instant flush),
 * the high-intent email capture submission, and the /api/platform/advisor
 * contract including x-platform-token auth. Presentation lives in
 * AdvisorChat.tsx. Conversation intentionally resets on full page reload.
 */
export function useAdvisorChat({
  domain,
  auditContext,
  toolResults,
  insights,
  actions,
  userTier = 'free',
}: UseAdvisorChatOptions) {
  const [messages, setMessages] = useState<AdvisorMessage[]>([])
  const [conversationHistory, setConversationHistory] = useState<
    { role: 'user' | 'assistant'; content: string }[]
  >([])
  const [loading, setLoading] = useState(false)
  const [typingId, setTypingId] = useState<number | null>(null)
  const [conversationId, setConversationId] = useState('')
  const [ctaDismissed, setCtaDismissed] = useState(false)
  const [showEmailCapture, setShowEmailCapture] = useState(false)
  const [emailSubmitting, setEmailSubmitting] = useState(false)
  const [emailSubmitted, setEmailSubmitted] = useState(false)
  const [intentCategory, setIntentCategory] = useState<string | null>(null)
  const typewriterRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const fullContentRef = useRef('')
  const prefersReducedMotion = usePrefersReducedMotion()

  // Generate conversation ID on mount
  useEffect(() => {
    setConversationId(crypto.randomUUID())
  }, [])

  // Count advisor responses. Free tier: hard cap at 5 messages (matches
  // pricing copy). Paid: no cap. Soft CTA retained for legacy engagement
  // tracking but only shown when not already gated.
  const advisorMessageCount = messages.filter(m => m.role === 'advisor').length
  const isFreeTier = userTier === 'free'
  const isGated = isFreeTier && advisorMessageCount >= FREE_TIER_MESSAGE_CAP
  const showSoftCTA = !isGated && advisorMessageCount >= 6 && !ctaDismissed

  // Ref-safe mirror of the gate so the canonical send path enforces the
  // free-tier cap even when invoked from a stale render/event closure
  // (Codex review 2026-07-11: UI-layer disabling alone is bypassable).
  const isGatedRef = useRef(isGated)
  useEffect(() => {
    isGatedRef.current = isGated
  }, [isGated])

  // Typewriter effect - 4 chars every 12ms; instant under reduced motion
  useEffect(() => {
    if (typingId === null) return

    if (prefersReducedMotion) {
      const full = fullContentRef.current
      setMessages(prev => {
        const idx = prev.findIndex(m => m.id === typingId)
        if (idx === -1) return prev
        const updated = [...prev]
        updated[idx] = { ...updated[idx], content: full }
        return updated
      })
      setTypingId(null)
      return
    }

    if (typewriterRef.current) clearInterval(typewriterRef.current)

    typewriterRef.current = setInterval(() => {
      const full = fullContentRef.current

      setMessages(prev => {
        const updated = [...prev]
        const idx = updated.findIndex(m => m.id === typingId)
        if (idx === -1) return prev

        const current = updated[idx].content
        if (current.length >= full.length) {
          if (typewriterRef.current) {
            clearInterval(typewriterRef.current)
            typewriterRef.current = null
          }
          setTypingId(null)
          return prev
        }

        const charsToAdd = Math.min(4, full.length - current.length)
        updated[idx] = {
          ...updated[idx],
          content: full.slice(0, current.length + charsToAdd),
        }
        return updated
      })
    }, 12)

    return () => {
      if (typewriterRef.current) clearInterval(typewriterRef.current)
    }
  }, [typingId, prefersReducedMotion])

  // Flush the active typewriter message to its full content immediately.
  // Used by the "Skip" affordance on the typing message and called at the
  // start of sendMessage so a new question never has to wait for the
  // previous animation (eval P3 #19 interruptible).
  const skipTypewriter = useCallback(() => {
    if (typingId === null) return
    const full = fullContentRef.current
    setMessages(prev => {
      const idx = prev.findIndex(m => m.id === typingId)
      if (idx === -1) return prev
      const updated = [...prev]
      updated[idx] = { ...updated[idx], content: full }
      return updated
    })
    if (typewriterRef.current) {
      clearInterval(typewriterRef.current)
      typewriterRef.current = null
    }
    setTypingId(null)
  }, [typingId])

  // The single canonical send path — every submission method routes here.
  const sendMessage = useCallback(
    async (question: string) => {
      if (!question.trim() || loading) return
      // Free-tier cap: enforced here in the canonical path, before the user
      // message is appended or /api/platform/advisor is called. The UI
      // (disabled inputs, external-trigger check) surfaces the upgrade gate.
      if (isGatedRef.current) return
      skipTypewriter()
      const userMsg: AdvisorMessage = {
        id: ++msgIdCounter,
        role: 'user',
        content: question.trim(),
      }
      setMessages(prev => [...prev, userMsg])
      setLoading(true)

      // Build request body with conversation history and rich context
      const requestBody: Record<string, unknown> = {
        question: question.trim(),
        domain,
        auditContext,
        conversationId,
        messages: conversationHistory,
      }

      // Include rich tool data on first message (or always if small enough)
      if (toolResults) requestBody.toolResults = toolResults
      if (insights) requestBody.insights = insights
      if (actions) requestBody.actions = actions

      try {
        // Authenticated route. platformFetch carries the session cookie and,
        // for token-only users, the x-platform-token header — the third
        // hand-rolled copy of that logic used to live here.
        const res = await platformFetch('/api/platform/advisor', {
          method: 'POST',
          body: JSON.stringify(requestBody),
        })
        const data = await res.json()
        if (res.ok) {
          const full = stripMarkdown(data.answer)
          const advisorId = ++msgIdCounter
          fullContentRef.current = full

          // Update conversation history for multi-turn
          setConversationHistory(prev => [
            ...prev,
            { role: 'user', content: question.trim() },
            { role: 'assistant', content: full },
          ])

          // Update conversation ID if server generated one
          if (data.conversationId) setConversationId(data.conversationId)

          // Check for high-intent signals (trigger email capture)
          if (data.intentSignals?.isHighIntent && !emailSubmitted) {
            setShowEmailCapture(true)
            setIntentCategory(data.intentSignals.category || null)
          }

          setMessages(prev => [
            ...prev,
            {
              id: advisorId,
              role: 'advisor',
              content: '',
              fullContent: full,
              relatedEntities: data.relatedEntities,
            },
          ])
          setTypingId(advisorId)
        } else {
          setMessages(prev => [
            ...prev,
            {
              id: ++msgIdCounter,
              role: 'advisor',
              content: data.message || 'Sorry, I could not process that.',
            },
          ])
        }
      } catch {
        setMessages(prev => [
          ...prev,
          {
            id: ++msgIdCounter,
            role: 'advisor',
            content: 'Network error. Please try again.',
          },
        ])
      }
      setLoading(false)
    },
    [
      loading,
      skipTypewriter,
      domain,
      auditContext,
      conversationId,
      conversationHistory,
      toolResults,
      insights,
      actions,
      emailSubmitted,
    ]
  )

  // Submit email for human-in-the-loop outreach
  const submitEmail = useCallback(
    async (email: string) => {
      if (!email.trim() || emailSubmitting) return
      setEmailSubmitting(true)
      try {
        // Build top issues from insights
        const topIssues = (insights || [])
          .filter(i => i.type === 'bad' || i.type === 'conflict')
          .slice(0, 5)
          .map(i => i.title)

        // Get unified score from tool results
        const completedScores = Object.values(toolResults || {})
          .filter(t => t.status === 'done' && t.data?.overallScore != null)
          .map(t => t.data.overallScore as number)
        const unifiedScore =
          completedScores.length > 0
            ? Math.round(
                completedScores.reduce((a, b) => a + b, 0) /
                  completedScores.length
              )
            : null

        await fetch('/api/platform/advisor-lead', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email.trim(),
            domain,
            unifiedScore,
            intentCategory,
            conversationId,
            conversation: conversationHistory,
            topIssues,
          }),
        })

        setEmailSubmitted(true)
        setShowEmailCapture(false)

        // Show confirmation as advisor message
        setMessages(prev => [
          ...prev,
          {
            id: ++msgIdCounter,
            role: 'advisor',
            content:
              'Got it. Our team will review your audit and reach out with specific recommendations for your site. Keep an eye on your inbox.',
          },
        ])
      } catch {
        // Silent fail, don't break the chat
      }
      setEmailSubmitting(false)
    },
    [
      emailSubmitting,
      insights,
      toolResults,
      domain,
      intentCategory,
      conversationId,
      conversationHistory,
    ]
  )

  return {
    messages,
    sendMessage,
    skipTypewriter,
    loading,
    typingId,
    advisorMessageCount,
    isGated,
    showSoftCTA,
    dismissCta: () => setCtaDismissed(true),
    showEmailCapture,
    hideEmailCapture: () => setShowEmailCapture(false),
    submitEmail,
    emailSubmitting,
    emailSubmitted,
  }
}
